/**
 * Zwei-Faktor-Client — die App-seitige, TYPISIERTE Verdrahtung des geteilten
 * Sicherheits-Tabs (`core-frontend` `plugin-security`).
 *
 * `core-frontend` kennt den `AppRouter` dieser App nicht; der Tab bekommt
 * seinen Client deshalb hereingereicht. Die Typpruefung passiert hier, wo
 * `trpc` gegen `AppRouter` gebunden ist — kein `any`, keine Sentinel-Typen.
 * Gleiches Muster wie `useEmailLogClient` und `useGovCredentialsClient`.
 *
 * 🔴 Alle drei Mutationen verlangen das PASSWORT, nicht einen TOTP-Code. Seit
 * core-backend 1.68.0 fuehrt dort better-auth (`auth.ts` → `enableTwoFactor`,
 * `disableTwoFactor`, `generateBackupCodes`), und better-auth verlangt es —
 * damit ein gekapertes Sitzungs-Cookie allein nicht genuegt, um einem
 * Eigentuemer einen zweiten Faktor unterzuschieben und ihn damit auszusperren.
 * Die Formen unten sind an den Router gemessen, nicht geraten.
 *
 * ⚪ `generate` liefert die Sicherungscodes; `enable` liefert immer `[]`. Der
 * Tab liest sie deshalb aus `generate` — das ist dort eigens bewacht.
 */

import type { TwoFactorApiClient } from '@xhubio-saas/core-frontend/plugin-security'
import { useMemo, useRef } from 'react'
import { trpc } from '@/trpc'

export function useTwoFactorClient(): TwoFactorApiClient {
  const utils = trpc.useUtils()
  const generateMutation = trpc.auth.twoFactor.generate.useMutation()
  const enableMutation = trpc.auth.twoFactor.enable.useMutation()
  const disableMutation = trpc.auth.twoFactor.disable.useMutation()
  const regenerateMutation = trpc.auth.twoFactor.regenerateBackupCodes.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns. (Gleiches Muster wie
  // `useGovCredentialsClient`.)
  const mutations = useRef({
    generateMutation,
    enableMutation,
    disableMutation,
    regenerateMutation,
  })
  mutations.current = { generateMutation, enableMutation, disableMutation, regenerateMutation }

  return useMemo<TwoFactorApiClient>(
    () => ({
      status: () => utils.auth.twoFactor.status.fetch(),

      generate: async (params) => {
        const r = await mutations.current.generateMutation.mutateAsync(params)
        // 🔴 Nach JEDER Mutation den Stand verwerfen — sonst zeigt der Tab
        // „aus", waehrend 2FA laengst scharf ist.
        await utils.auth.twoFactor.status.invalidate()
        return r
      },

      enable: async (params) => {
        const r = await mutations.current.enableMutation.mutateAsync(params)
        await utils.auth.twoFactor.status.invalidate()
        return { success: r.success, message: r.message }
      },

      disable: async (params) => {
        const r = await mutations.current.disableMutation.mutateAsync(params)
        await utils.auth.twoFactor.status.invalidate()
        return { success: r.success, message: r.message }
      },

      regenerateBackupCodes: async (params) => {
        const r = await mutations.current.regenerateMutation.mutateAsync(params)
        await utils.auth.twoFactor.status.invalidate()
        return r
      },
    }),
    [utils]
  )
}
